import { CircleCheckBig, Copy } from 'lucide-react';
import * as React from 'react';
import { toast } from 'sonner';

import { Button } from '@/_shared/components/ui/button';
import { cn } from '@/_shared/lib/utils';

/** How long the inline "copied" check is shown before reverting to the copy icon. */
const COPIED_REVERT_MS = 2000;

export interface CopyButtonProps extends Omit<React.ComponentProps<typeof Button>, 'onClick'> {
  /** Text written to the clipboard on click. */
  text: string;
  /** Names the copied content in the success toast (e.g. "Setup SQL" → "Setup SQL copied"). */
  copyLabel?: string;
  /** Override the idle (not-yet-copied) icon. Defaults to the copy glyph. Omit
   *  Lucide `size` / `size-*` — `Button` owns icon sizing. The copied-state check
   *  (when `copiedFeedback` is on) is not affected. */
  copyIcon?: React.ReactNode;
  /**
   * Confirm a successful copy inline: the icon morphs to a check for ~2s and the
   * accessible name becomes "Copied", instead of firing the success toast. The
   * failure toast still fires on error. Off by default — callers opt in when the
   * button is visible at the moment of copy (the toast is the better signal for
   * buttons that may be scrolled off-screen). */
  copiedFeedback?: boolean;
}

/**
 * Copy-to-clipboard button. By default, success/failure feedback is a sonner toast,
 * so this requires a `<Toaster>` mounted in the tree — today a single app-wide one at
 * the App root (`ui/src/App.tsx`), so every route is covered. Pass `copiedFeedback`
 * to replace the success toast with an inline check-mark confirmation.
 */
export function CopyButton({
  text,
  copyLabel,
  copyIcon,
  copiedFeedback = false,
  variant = 'secondary',
  size = 'sm',
  label,
  'aria-label': ariaLabel,
  ...props
}: CopyButtonProps): React.JSX.Element {
  const [copied, setCopied] = React.useState(false);
  const revertTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  // Clear a pending revert on unmount so it can't setState on a gone component.
  React.useEffect(() => {
    return () => {
      if (revertTimerRef.current) clearTimeout(revertTimerRef.current);
    };
  }, []);

  const onCopy = React.useCallback(async () => {
    try {
      if (!navigator.clipboard?.writeText) {
        throw new Error('Clipboard unavailable');
      }
      await navigator.clipboard.writeText(text);
      if (copiedFeedback) {
        setCopied(true);
        // Reset any in-flight revert so rapid clicks don't flip back early.
        if (revertTimerRef.current) clearTimeout(revertTimerRef.current);
        revertTimerRef.current = setTimeout(() => setCopied(false), COPIED_REVERT_MS);
      } else {
        toast.success(copyLabel ? `${copyLabel} copied` : 'Copied');
      }
    } catch {
      toast.error('Failed to copy to clipboard');
    }
  }, [text, copyLabel, copiedFeedback]);

  const showCopied = copiedFeedback && copied;
  // Icon-only buttons need an accessible name; a visible label already provides one.
  const baseAriaLabel = ariaLabel ?? (label != null ? undefined : copyLabel ? `Copy ${copyLabel}` : 'Copy');
  const idleIcon = copyIcon ?? <Copy />;

  // With inline feedback, crossfade between the idle icon and the check so both
  // the enter (copy) and exit (revert) transitions animate. Both layers stay
  // mounted and toggle opacity/scale — an unmounted node can't animate out.
  const layer =
    'absolute inset-0 inline-flex items-center justify-center transition-all duration-200 ease-out motion-reduce:transition-none';
  const icon = copiedFeedback ? (
    <span className="relative inline-flex size-4 shrink-0">
      <span data-copy-icon="idle" className={cn(layer, showCopied ? 'scale-50 opacity-0' : 'scale-100 opacity-100')}>
        {idleIcon}
      </span>
      <span data-copy-icon="check" className={cn(layer, showCopied ? 'scale-100 opacity-100' : 'scale-50 opacity-0')}>
        <CircleCheckBig className="text-success" />
      </span>
    </span>
  ) : (
    idleIcon
  );

  return (
    <>
      {copiedFeedback ? (
        <span className="sr-only" aria-live="polite" aria-atomic="true">
          {showCopied ? 'Copied' : ''}
        </span>
      ) : null}
      <Button
        type="button"
        variant={variant}
        size={size}
        label={label}
        // Success is announced once via the aria-live region above; keep the
        // accessible name stable so screen readers don't double-announce "Copied".
        aria-label={baseAriaLabel}
        {...props}
        Icon={icon}
        onClick={() => void onCopy()}
      />
    </>
  );
}
